import { useState } from 'react';
import { Shell } from '../../components/layout/Shell.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Tabs } from '../../components/ui/Tabs.jsx';
import { C } from '../../theme/palette';
import { localeDates } from '../../lib/i18n';
import { avancementDuChantier } from '../../lib/costs';
import { nomComplet } from '../../lib/acces';
import { nomDuChantier } from '../../lib/nom-chantier';

export function PlanningPage({
  onNav,
  soc,
  chantiers,
  setChantiers,
  employes,
  conges,
  estMixte,
  univers,
  setUnivers,
}) {
  const emps = employes || [];
  const [vue, setVue] = useState('agenda');
  const [pick, setPick] = useState(null);
  const upd = (id, fn) =>
    setChantiers && setChantiers((p) => p.map((c) => (c.id === id ? fn(c) : c)));
  const actifs = (chantiers || []).filter((c) => c.statut !== 'Terminé' && c.statut !== 'Annulé');
  const pad = (n) => String(n).padStart(2, '0');
  const iso = (d) => d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  const parse = (s) => {
    if (!s) return null;
    const a = s.split('-').map(Number);
    return new Date(a[0], a[1] - 1, a[2]);
  };
  const [wk, setWk] = useState(() => {
    const d = new Date();
    const off = (d.getDay() + 6) % 7;
    d.setDate(d.getDate() - off);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const jours = [0, 1, 2, 3, 4, 5, 6].map((i) => {
    const d = new Date(wk);
    d.setDate(d.getDate() + i);
    return d;
  });
  const JN = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
  const empName = (id) => {
    const e = emps.find((x) => x.id === id);
    // Prénom et nom : deux « Test » de la même famille ne se distinguaient pas.
    return e ? nomComplet(e) || e.nom : '#' + id;
  };
  const empCol = (id) => {
    const e = emps.find((x) => x.id === id);
    return e ? e.col || C.grey : C.grey;
  };
  const initiales = (nom) =>
    (nom || '')
      .split(' ')
      .map((w) => w[0] || '')
      .join('')
      .slice(0, 2)
      .toUpperCase();
  const empsOn = (ch, d) => {
    const k = iso(d);
    const ids = [];
    (ch.pointages || []).forEach((p) => {
      if (p.date === k && p.type !== 'absence' && ids.indexOf(p.empId) < 0) ids.push(p.empId);
    });
    (ch.planif || []).forEach((p) => {
      if (p.date === k && ids.indexOf(p.empId) < 0) ids.push(p.empId);
    });
    return ids;
  };
  const estReel = (ch, k, empId) =>
    (ch.pointages || []).some((p) => p.date === k && p.empId === empId && p.type !== 'absence');
  const toggle = (chId, k, empId) =>
    upd(chId, (c) => {
      const has = (c.planif || []).some((x) => x.date === k && x.empId === empId);
      return {
        ...c,
        planif: has
          ? (c.planif || []).filter((x) => !(x.date === k && x.empId === empId))
          : [...(c.planif || []), { date: k, empId }],
      };
    });
  const semLabel =
    jours[0].toLocaleDateString(localeDates(), { day: '2-digit', month: 'short' }) +
    ' – ' +
    jours[6].toLocaleDateString(localeDates(), { day: '2-digit', month: 'short' });

  // Fenêtre pour la frise
  const bornes = (ch) => {
    let d0 = ch.debut ? parse(ch.debut) : null,
      d1 = ch.fin ? parse(ch.fin) : null;
    if (!d0 || !d1) {
      const ds = (ch.pointages || []).map((p) => parse(p.date)).filter(Boolean);
      if (ds.length) {
        if (!d0) d0 = new Date(Math.min.apply(null, ds));
        if (!d1) d1 = new Date(Math.max.apply(null, ds));
      }
    }
    return { d0, d1 };
  };
  const toutes = [];
  actifs.forEach((c) => {
    const b = bornes(c);
    if (b.d0) toutes.push(b.d0);
    if (b.d1) toutes.push(b.d1);
  });
  let win0, win1;
  if (toutes.length) {
    win0 = new Date(Math.min.apply(null, toutes));
    win1 = new Date(Math.max.apply(null, toutes));
  } else {
    win0 = new Date();
    win1 = new Date();
    win1.setDate(win1.getDate() + 30);
  }
  win0 = new Date(win0);
  win0.setDate(win0.getDate() - 3);
  win1 = new Date(win1);
  win1.setDate(win1.getDate() + 3);
  const span = Math.max(1, (win1 - win0) / 86400000);
  const pct = (d) => Math.max(0, Math.min(100, ((d - win0) / 86400000 / span) * 100));
  const ticks = [];
  {
    const t = new Date(win0);
    while (t <= win1) {
      ticks.push(new Date(t));
      t.setDate(t.getDate() + 7);
    }
  }

  return (
    <Shell
      active="plus"
      onNav={onNav}
      soc={soc}
      pageTitle="📆 Planning"
      estMixte={estMixte}
      univers={univers}
      setUnivers={setUnivers}
    >
      <div style={{ marginBottom: 12 }}>
        <Tabs
          items={[
            ['agenda', '📅 Agenda semaine'],
            ['frise', '📊 Frise / Gantt'],
          ]}
          active={vue}
          onChange={setVue}
        />
      </div>

      {actifs.length === 0 && (
        <Card>
          <div style={{ padding: 22, textAlign: 'center', color: C.muted, fontSize: 11 }}>
            Aucun chantier en cours. Le planning affiche les chantiers actifs.
          </div>
        </Card>
      )}

      {vue === 'agenda' && actifs.length > 0 && (
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 10,
            }}
          >
            <button
              onClick={() => {
                const d = new Date(wk);
                d.setDate(d.getDate() - 7);
                setWk(d);
              }}
              style={{
                background: C.white,
                border: '1px solid ' + C.border,
                borderRadius: 9,
                padding: '7px 12px',
                cursor: 'pointer',
                fontWeight: 700,
              }}
            >
              ←
            </button>
            <div style={{ fontSize: 11, fontWeight: 800 }}>{semLabel}</div>
            <button
              onClick={() => {
                const d = new Date(wk);
                d.setDate(d.getDate() + 7);
                setWk(d);
              }}
              style={{
                background: C.white,
                border: '1px solid ' + C.border,
                borderRadius: 9,
                padding: '7px 12px',
                cursor: 'pointer',
                fontWeight: 700,
              }}
            >
              →
            </button>
          </div>
          <div style={{ fontSize: 8, color: C.muted, marginBottom: 8 }}>
            Plein = présence pointée · contour = planifié. Touche une case pour affecter quelqu'un.
          </div>
          <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
            <div style={{ minWidth: 620 }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '120px repeat(7,1fr)',
                  gap: 4,
                  marginBottom: 4,
                }}
              >
                <div />
                {jours.map((d, i) => (
                  <div
                    key={i}
                    style={{ textAlign: 'center', fontSize: 8.5, fontWeight: 700, color: C.muted }}
                  >
                    {JN[i]}
                    <div style={{ fontSize: 8, color: C.muted }}>
                      {d.getDate()}/{d.getMonth() + 1}
                    </div>
                  </div>
                ))}
              </div>
              {actifs.map((ch) => (
                <div
                  key={ch.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '120px repeat(7,1fr)',
                    gap: 4,
                    marginBottom: 4,
                  }}
                >
                  <div
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      paddingRight: 4,
                    }}
                  >
                    {nomDuChantier(ch, actifs)}
                  </div>
                  {jours.map((d, i) => {
                    const k = iso(d);
                    const ids = empsOn(ch, d);
                    return (
                      <div
                        key={i}
                        onClick={() => setPick({ chId: ch.id, k })}
                        style={{
                          minHeight: 38,
                          background: C.white,
                          border: '1px solid ' + C.border,
                          borderRadius: 8,
                          padding: 3,
                          cursor: 'pointer',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 2,
                          alignContent: 'flex-start',
                        }}
                      >
                        {ids.map((id) => {
                          const reel = estReel(ch, k, id);
                          return (
                            <span
                              key={id}
                              title={empName(id)}
                              style={{
                                fontSize: 7.5,
                                fontWeight: 800,
                                color: reel ? '#fff' : empCol(id),
                                background: reel ? empCol(id) : empCol(id) + '22',
                                border: '1px solid ' + empCol(id),
                                borderRadius: 5,
                                padding: '1px 4px',
                                lineHeight: 1.5,
                              }}
                            >
                              {initiales(empName(id))}
                            </span>
                          );
                        })}
                        {ids.length === 0 && (
                          <span style={{ fontSize: 13, color: C.border, margin: 'auto' }}>+</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
              {(conges || []).some((c) => c.statut === 'Approuvé') && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '120px repeat(7,1fr)',
                    gap: 4,
                    marginBottom: 4,
                  }}
                >
                  <div
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      paddingRight: 4,
                      color: C.teal,
                    }}
                  >
                    🌴 Congés
                  </div>
                  {jours.map((d, i) => {
                    const k = iso(d);
                    const ids = (conges || [])
                      .filter(
                        (c) =>
                          c.statut === 'Approuvé' &&
                          c.debut &&
                          k >= c.debut &&
                          k <= (c.fin || c.debut),
                      )
                      .map((c) => c.empId);
                    return (
                      <div
                        key={i}
                        style={{
                          minHeight: 32,
                          background: C.tealL,
                          border: '1px solid ' + C.teal + '44',
                          borderRadius: 8,
                          padding: 3,
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 2,
                          alignContent: 'flex-start',
                        }}
                      >
                        {ids.map((id) => (
                          <span
                            key={id}
                            title={empName(id)}
                            style={{
                              fontSize: 7.5,
                              fontWeight: 800,
                              color: C.teal,
                              background: '#fff',
                              border: '1px solid ' + C.teal,
                              borderRadius: 5,
                              padding: '1px 4px',
                              lineHeight: 1.5,
                            }}
                          >
                            {initiales(empName(id))}
                          </span>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          {pick &&
            (() => {
              const ch = actifs.find((c) => c.id === pick.chId);
              if (!ch) return null;
              return (
                <Card style={{ marginTop: 12, border: '2px solid ' + C.navy }}>
                  <Bar
                    icon="👷"
                    title={'Affecter — ' + nomDuChantier(ch, actifs)}
                    color={C.navy}
                    sub={pick.k.split('-').reverse().join('.')}
                  />
                  <div style={{ padding: 12 }}>
                    {emps.length === 0 && (
                      <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
                        Aucun employé enregistré. Ajoute ton équipe depuis l’onglet Équipe pour
                        pouvoir affecter quelqu’un à ce chantier.
                      </div>
                    )}
                    {emps.map((e) => {
                      const on = empsOn(ch, parse(pick.k)).indexOf(e.id) >= 0;
                      const reel = estReel(ch, pick.k, e.id);
                      return (
                        <div
                          key={e.id}
                          onClick={() => {
                            if (!reel) toggle(ch.id, pick.k, e.id);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 9,
                            padding: '9px 6px',
                            borderBottom: '1px solid ' + C.border,
                            cursor: reel ? 'default' : 'pointer',
                            opacity: reel ? 0.7 : 1,
                          }}
                        >
                          <span
                            style={{
                              width: 22,
                              height: 22,
                              borderRadius: 6,
                              background: e.col || C.grey,
                              color: '#fff',
                              fontSize: 8,
                              fontWeight: 800,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {initiales(nomComplet(e) || e.nom)}
                          </span>
                          <span style={{ flex: 1, fontSize: 10.5, fontWeight: 600 }}>
                            {nomComplet(e) || e.nom}
                            {e.role && (
                              <span style={{ color: C.muted, fontWeight: 400 }}> · {e.role}</span>
                            )}
                          </span>
                          {reel ? (
                            <span style={{ fontSize: 8, color: C.green, fontWeight: 700 }}>
                              pointé
                            </span>
                          ) : (
                            <span style={{ fontSize: 14, color: on ? C.green : C.border }}>
                              {on ? '✓' : '+'}
                            </span>
                          )}
                        </div>
                      );
                    })}
                    <div style={{ marginTop: 10 }}>
                      <Btn label="Fermer" ghost color={C.grey} full onClick={() => setPick(null)} />
                    </div>
                  </div>
                </Card>
              );
            })()}
        </div>
      )}

      {vue === 'frise' && actifs.length > 0 && (
        <div>
          <div style={{ fontSize: 8, color: C.muted, marginBottom: 8 }}>
            Chaque barre = durée du chantier. Règle les dates si besoin ; la couleur remplie indique
            l'avancement.
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 8,
              color: C.muted,
              marginBottom: 4,
              paddingLeft: 4,
            }}
          >
            <span>
              {win0.toLocaleDateString(localeDates(), { day: '2-digit', month: 'short' })}
            </span>
            <span>
              {win1.toLocaleDateString(localeDates(), { day: '2-digit', month: 'short' })}
            </span>
          </div>
          {actifs.map((ch) => {
            const b = bornes(ch);
            const has = b.d0 && b.d1;
            const left = has ? pct(b.d0) : 0;
            const width = has ? Math.max(2, pct(b.d1) - left) : 0;
            return (
              <Card key={ch.id} style={{ marginBottom: 9 }}>
                <div style={{ padding: '10px 12px' }}>
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}
                  >
                    <span style={{ fontSize: 10, fontWeight: 800 }}>
                      {nomDuChantier(ch, actifs)}
                    </span>
                    <span style={{ fontSize: 9, color: C.muted }}>{avancementDuChantier(ch)}%</span>
                  </div>
                  <div
                    style={{
                      position: 'relative',
                      height: 18,
                      background: C.navyXL,
                      borderRadius: 6,
                      overflow: 'hidden',
                    }}
                  >
                    {ticks.map((t, i) => (
                      <div
                        key={i}
                        style={{
                          position: 'absolute',
                          left: pct(t) + '%',
                          top: 0,
                          bottom: 0,
                          width: 1,
                          background: C.border,
                        }}
                      />
                    ))}
                    {has ? (
                      <div
                        style={{
                          position: 'absolute',
                          left: left + '%',
                          width: width + '%',
                          top: 2,
                          bottom: 2,
                          background: C.green + '33',
                          border: '1px solid ' + C.green,
                          borderRadius: 5,
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            width: avancementDuChantier(ch) + '%',
                            height: '100%',
                            background: C.green + '99',
                          }}
                        />
                      </div>
                    ) : (
                      <div
                        style={{
                          position: 'absolute',
                          left: 6,
                          top: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: 8,
                          color: C.muted,
                        }}
                      >
                        dates non définies
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 7.5, color: C.muted, marginBottom: 2 }}>Début</div>
                      <input
                        type="date"
                        value={ch.debut || ''}
                        onChange={(e) => upd(ch.id, (c) => ({ ...c, debut: e.target.value }))}
                        style={{
                          width: '100%',
                          border: '1px solid ' + C.border,
                          borderRadius: 7,
                          padding: '6px',
                          fontSize: 9.5,
                        }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 7.5, color: C.muted, marginBottom: 2 }}>Fin</div>
                      <input
                        type="date"
                        value={ch.fin || ''}
                        onChange={(e) => upd(ch.id, (c) => ({ ...c, fin: e.target.value }))}
                        style={{
                          width: '100%',
                          border: '1px solid ' + C.border,
                          borderRadius: 7,
                          padding: '6px',
                          fontSize: 9.5,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </Shell>
  );
}
